/* eslint-disable @typescript-eslint/no-explicit-any */
import { CancelablePromise } from 'src/types/upload';
import { makeCancelable } from './fetchClient';
import { getSdk } from 'src/sdk';
import { logError } from 'src/loggingManager';
import { parseError } from 'src/util/misc';

const TENANT = 'default';

const uploadsDomains: Record<string, string> = {
    'us-east': 'https://cluster-110.uploads.documents.cimpress.io',
    'eu-west': 'https://cluster-100.uploads.documents.cimpress.io',
    'ap-northeast': 'https://cluster-120.uploads.documents.cimpress.io',
    china: 'https://uploads.yinshida.com.cn',
    auto: 'https://uploads.documents.cimpress.io',
};

function getDomain(region = 'auto') {
    return uploadsDomains[region] || uploadsDomains.auto;
}

export function generateUploadsUrl(region = 'auto', uploadId = '', resizeParams?: Record<string, any>) {
    let url = `${getDomain(region)}/v1/uploads/${uploadId}?tenant=${TENANT}`;
    if (resizeParams) {
        url += `&transform=${encodeURIComponent(
            `[{"transform":"scaleDown","arguments":{x:${resizeParams.width},y:${resizeParams.height}}}]`,
        )}`;
    }
    return url;
}

// Uploads a single file through the SDK. Returns the upload id; cancelable via `.cancel` / saga `[CANCEL]`.
function uploadFile(formData: FormData, label: string): CancelablePromise<string> {
    return makeCancelable((signal) =>
        getSdk()
            .Uploads.uploadBinaryFile({ data: formData, tenant: TENANT, signal })
            .then(({ uploadId }) => uploadId)
            .catch((e) => {
                // cancelled request never settles, so callers don't see a fake failure
                if (signal.aborted) return new Promise<string>(() => {});
                logError(`upload ${label} failed! ${parseError(e)}`);
                throw e;
            }),
    );
}

// Returns the upload id
export function uploadSceneXml(xml: string): CancelablePromise<string> {
    const formData = new FormData();
    formData.append(
        'file',
        new Blob([`<?xml version="1.0" encoding="utf-16"?>${xml}`], { type: 'text/xml' }),
        'index.xml',
    );
    return uploadFile(formData, 'scene xml');
}

// Returns the upload id
export function uploadJSONFile(json: Record<string, any>): CancelablePromise<string> {
    const formData = new FormData();
    formData.append('file', new Blob([JSON.stringify(json)], { type: 'application/json' }), 'document.json');
    return uploadFile(formData, 'json');
}

// Returns the upload id
export function uploadImage(file: File): CancelablePromise<string> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    return uploadFile(formData, 'image');
}

export function getUrlById(uploadId: string) {
    return `${uploadsDomains.auto}/v1/uploads/${uploadId}`;
}
